import React, { useEffect } from 'react'
import PageBody from '../../../components/pagebody/PageBody'
import Button from '../../../components/button/Button'
import { useDispatch, useSelector } from 'react-redux'
import { componentKey, setClientPayerId, setFormList, setGridData, setGridSelectedForms, setPayerList, setRowsPerPage, setSelectedForm, setSelectedPayer, setSelectedYear, setSkip, setYearList } from './GenerateSummarySlice'
import styles from './GenerateSummary.styles'
import Loadable from '../../../components/loadable/Loadable'
import useGarbageCollector from '../../../../libs/garbagecollector/garbageCollectorHooks'
import SelectDropdown from '../../../components/dropdown/selectdropdown/SelectDropdown'
import Label from '../../../components/label/Label'
import Heading from '../../../components/heading/Heading'
import { HEADING } from '../../../components/heading/constants/constants'
import { downloadAllPDFs, downloadPDFsForAllPayers, downloadPDFsForSelectedPayer, getFilterData, getPdfPreview, getSummaryData } from './GenerateSummarySaga'
import TextInput from '../../../components/input/textinput/TextInput'
import RichGrid from '../../../components/richgrid/RichGrid'
import { ROW_SELECTION_OPTIONS } from '../../../components/richgrid/constants'
import { LABEL_WEIGHT } from '../../../components/label/constants'
import { BUTTON_TYPE } from '../../../components/button/constants'
import Icons from '../../../components/icons/Icons'
import { VEC_ICON_NAME } from '../../../components/icons/constants'
import General from '../../../../libs/utility/General';
import { COMMON_EXPORTING_STATES, COMMON_EXPORTING_STATES_MESSAGE_MAP, COMMON_LOADING_STATES, COMMON_LOADING_STATES_MESSAGE_MAP, PAGE_STATE, PAGE_STATE_MESSAGE_MAP } from '../../constants/constants'
import { addNotifications } from '../../toaster/ToasterPortalSlice'
import useFormatTIN from '../../../../libs/customhooks/useFormatTIN'
import useDelayedAction from '../../../../libs/customhooks/useDelayedAction'
import { GENERATE_SUMMARY_LOADING_STATES, GENERATE_SUMMARY_LOADING_STATES_MESSAGE_MAP } from './constants'

export default function GenerateSummary() {

    useGarbageCollector(componentKey);
    const { loadingState, selectedPayer, exportFile, payerList, yearList, selectedYear, formList, selectedForm, clientPayerId, gridData, rowsPerPage, skip, totalData, gridSelectedForms } = useSelector((state) => state[componentKey]);
    const dispatch = useDispatch();

    const { formatTIN } = useFormatTIN()

    useEffect(() => {
        dispatch(getFilterData());
    }, [])

    const handleChangeSelect = (name, selectedOption) => {
        switch(name){
            case 'payer':
                dispatch(setSelectedPayer(selectedOption))
                break;
            case 'year':
                dispatch(setSelectedYear(selectedOption))
                break;
            case 'form':
                dispatch(setSelectedForm(selectedOption))
                break;
        }
    }
    
    const handleSearch = (e) => {
        dispatch(setClientPayerId(e.target.value))
    }

    const handleGetSummaryGridData = useDelayedAction((skip, rowsPerPage, clientPayerId, selectedPayer, selectedYear, selectedForm) => {
        const params = {
            page: (skip / rowsPerPage) + 1,
            pageSize: rowsPerPage,
            payerId: selectedPayer.value,
            taxYear: selectedYear.value,
            FormType: selectedForm.value,
            ClientPayerId: clientPayerId
        }
        dispatch(getSummaryData(params))
    })

    useEffect(() => {
        if( selectedPayer && selectedPayer?.value !== "" && selectedYear && selectedYear?.value !== "" && selectedForm && selectedForm?.value !== "" ) {
            handleGetSummaryGridData(0, rowsPerPage, clientPayerId, selectedPayer, selectedYear, selectedForm)
            dispatch(setSkip(0));
        }
    }, [clientPayerId, selectedForm, selectedPayer, selectedYear])

    useEffect(() => {
        if (exportFile) {
            General.downloadFile(exportFile.Base64, exportFile.Name, exportFile.MimeType)
        }
    }, [exportFile])
    
    const validateDownload = () => {
        if(!selectedPayer || !selectedPayer.value || selectedPayer.value === "") {
            dispatch(addNotifications({ 
                heading: "Payer Not Selected", 
                message: "Please Select a Payer" 
            }))
            return false;
        }

        if(!selectedForm || !selectedForm.value || selectedForm.value === "") {
            dispatch(addNotifications({ 
                heading: "Form Not Selected", 
                message: "Please Select a Form" 
            }))
            return false;
        }

        if(!selectedYear || !selectedYear.value || selectedYear.value === "") {
            dispatch(addNotifications({ 
                heading: "Year Not Selected", 
                message: "Please Select a Year" 
            }))
            return false;
        }
        return true;
    }

    const handleDownloadRow = (row) => {
        dispatch(getPdfPreview({
            referenceNo: row.EncryptedReferenceNo,
            UserId: row.EncryptedUserid,
            formtype: row.FormName,
        }))
    }

    const handleDownloadAllPDF = () => {
        if(validateDownload()) {
            if(gridSelectedForms.length){
                dispatch(downloadAllPDFs({
                    ReferenceNo: gridSelectedForms.map(row => row.ReferenceNo),
                    payerId: selectedPayer.value,
                    taxYear: selectedYear.value,
                    formtype: selectedForm.value
                }))
            }else{
                dispatch(addNotifications({ 
                    heading: "No Record Selected", 
                    message: "Please select records to download" 
                }))
            }
        }
    }
    
    const handleDownloadForPayer = () => {
        if(validateDownload()) {
            dispatch(downloadPDFsForSelectedPayer({
                payerId: selectedPayer.value,
                taxYear: selectedYear.value,
                formtype: selectedForm.value
            }))
        }
    }
    const handleDownloadForAllPayer = () => {
        if(validateDownload()) {
            dispatch(downloadPDFsForAllPayers({
                taxYear: selectedYear.value,
                formtype: selectedForm.value
            }))
        }
    }

    const GENERATE_SUMMARY_COLUMNS = [
        {
            field: "referenceNo",
            header: <Label weight={LABEL_WEIGHT[700]} customStyles={styles.text}>Reference No</Label>,
            renderLogic: (row, idx) => <span style={styles.text}>{ row.ReferenceNo }</span>
        },
        {
            field: "payerName",
            header: <Label weight={LABEL_WEIGHT[700]} customStyles={styles.text}>Payer Name</Label>,
            renderLogic: (row, idx) => <span style={styles.text}>{ row.PayerName }</span>
        },
        {
            field: "payerTaxId",
            header: <Label weight={LABEL_WEIGHT[700]} customStyles={styles.text}>Payer Tax ID</Label>,
            renderLogic: (row, idx) => <span style={styles.text}>{ formatTIN(row.TINType, row.PayerEIN) }</span>
        },
        {
            field: "TaxYear",
            header: <Label weight={LABEL_WEIGHT[700]} customStyles={styles.text}>Tax Year</Label>,
            renderLogic: (row, idx) => <span style={styles.text}>{ row.TaxYear }</span>
        },
        {
            field: "clientPayerId",
            header: <Label weight={LABEL_WEIGHT[700]} customStyles={styles.text}>Client Payer ID</Label>,
            renderLogic: (row, idx) => <span style={styles.text}>{ row.ClientPayerId }</span>
        },
        {
            field: "formName",
            header: <Label weight={LABEL_WEIGHT[700]} customStyles={styles.text}>Form Name</Label>,
            renderLogic: (row, idx) => <span style={styles.text}>{ row.FormName }</span>
        },
        {
            field: "formCount",
            header: <Label weight={LABEL_WEIGHT[700]} customStyles={styles.text}>Form Count</Label>,
            renderLogic: (row, idx) => <span style={styles.text}>{ row.FormCount }</span>
        },
        {
            field: "download",
            header: <Label weight={LABEL_WEIGHT[700]} customStyles={styles.text}>Download 1096, 1094B/C, W-3, and W-3SS</Label>,
            renderLogic: (row, idx) =>
                <Loadable currentState={loadingState.state} loadingStates={`${COMMON_EXPORTING_STATES.EXPORT_LOADING}-${row.EncryptedReferenceNo}`} messageMap={COMMON_LOADING_STATES_MESSAGE_MAP} isNewLoadable={true} isFullWidth={false}>
                    <Button type={BUTTON_TYPE.LIGHT_WITH_NO_BORDER} title={`${row.FormName} PDF`} onClickCb={() => handleDownloadRow(row)}><Icons familyName={'vec'} iconName={VEC_ICON_NAME.DOWNLOAD_ICON} />
                    </Button>
                </Loadable>
        },
    ]

    return (
            <PageBody>
                <Heading type={HEADING.H1}>Generate Summary</Heading>
                <div style={styles.mainContainer}>
                <Loadable currentState={loadingState.state} loadingStates={COMMON_LOADING_STATES.FILTER_LOADING} messageMap={COMMON_LOADING_STATES_MESSAGE_MAP} isNewLoadable={true}>
                    <div style={styles.filtersRow}>
                        { 
                            payerList && selectedPayer &&
                            <div>
                                <Label>Payer</Label>
                                <SelectDropdown
                                    name="payer"
                                    placeHolder="Select Payer"
                                    value={selectedPayer}
                                    onSelectCb={(name, selectedOption) => handleChangeSelect(name, selectedOption)}
                                    isClearable={false}
                                    options={payerList}
                                />
                            </div>
                        }
                        { 
                            yearList && selectedYear &&
                            <div>
                                <Label>Year</Label>
                                <SelectDropdown
                                    name="year"
                                    placeHolder="Select Year"
                                    value={selectedYear}
                                    onSelectCb={(name, selectedOption) => handleChangeSelect(name, selectedOption)}
                                    isClearable={false}
                                    options={yearList}
                                />
                            </div>
                        }
                        { 
                            formList && selectedForm &&
                            <div>
                                <Label>Form</Label>
                                <SelectDropdown
                                    name="form"
                                    placeHolder="Selected Form"
                                    value={selectedForm}
                                    onSelectCb={(name, selectedOption) => handleChangeSelect(name, selectedOption)}
                                    isClearable={false}
                                    options={formList}
                                />
                        </div>
                        }

                    </div>
                </Loadable>
                <div style={styles.actionRow}>
                    <div style={styles.actionsRowLeftColumn}>
                        <Loadable currentState={loadingState.state} loadingStates={[GENERATE_SUMMARY_LOADING_STATES.DOWNLOAD_READY_FOR_ALL_PDFS_LOADING]} messageMap={GENERATE_SUMMARY_LOADING_STATES_MESSAGE_MAP} isNewLoadable={true} isFullWidth={false}>
                            <Button onClickCb={handleDownloadAllPDF}>Download All PDFs</Button>
                        </Loadable>
                        <Loadable currentState={loadingState.state} loadingStates={[GENERATE_SUMMARY_LOADING_STATES.DOWNLOAD_READY_ALL_PDFS_FOR_PAYERS_LOADING]} messageMap={GENERATE_SUMMARY_LOADING_STATES_MESSAGE_MAP} isNewLoadable={true} isFullWidth={false}>
                            <Button onClickCb={handleDownloadForPayer}>Download All PDFs for Payer</Button>
                        </Loadable>
                        <Loadable currentState={loadingState.state} loadingStates={[GENERATE_SUMMARY_LOADING_STATES.DOWNLOAD_READY_PDFS_FOR_ALL_PAYERS_LOADING]} messageMap={GENERATE_SUMMARY_LOADING_STATES_MESSAGE_MAP} isNewLoadable={true} isFullWidth={false}>
                            <Button onClickCb={handleDownloadForAllPayer}>Download PDFs for All Payers</Button>
                        </Loadable>
                    </div>
                    <div>
                        <Label>Search Client Payer ID:</Label>
                        <TextInput
                            name='search'
                            value={clientPayerId}
                            placeHolder='Search Client Payer ID'
                            onChangeCb={(e) => handleSearch(e)}
                        />
                    </div>
                </div>
                <Loadable currentState={loadingState.state} loadingStates={COMMON_LOADING_STATES.GRID_LOADING} messageMap={COMMON_LOADING_STATES_MESSAGE_MAP} isNewLoadable={true}>
                    <RichGrid
                        selectable={true}
                        data={gridData}
                        columns={GENERATE_SUMMARY_COLUMNS}
                        extractRowKey={(row) => row.ReferenceNo}
                        selectedRows={gridSelectedForms}
                        onHeaderSelectionChangeCallBack={(selectedData) => dispatch(setGridSelectedForms(selectedData))}
                        onSelectionChangeCallBack={(selectedData) => dispatch(setGridSelectedForms(selectedData))}
                        paginationProps={{
                            skip: skip,
                            take: rowsPerPage,
                            takeOptions: ROW_SELECTION_OPTIONS,
                            onChangeTakeCb: (newTake) => {
                                dispatch(setRowsPerPage(newTake));
                                dispatch(setSkip(0));
                                handleGetSummaryGridData(0, newTake, clientPayerId, selectedPayer, selectedYear, selectedForm)
                            },
                            total: totalData,
                            onChange: (take, t) => {
                                dispatch(setRowsPerPage(take));
                                dispatch(setSkip(t));
                                handleGetSummaryGridData(t, take, clientPayerId, selectedPayer, selectedYear, selectedForm)
                            },
                        }}
                    />
                </Loadable>
                </div>
            </PageBody>
  )
}